import staticDefaultRemedyCategory from '@/assets/data/default-remedy-category.json' // DEPRECATED
import {
  IBaseEffect,
  IBaseMappedRemedy,
  IBaseRemedy,
  IBaseRemedyReference,
  ICost,
  IEffectRemedy,
  IMappedRemedy,
  IRemedy,
  IRemedyCategory,
  IRemedyCost,
  IRemedyEffect,
  IRemedyReference,
  TEffectLevel,
  TFootprintPlacement
} from '@lifk/types'
import { isNumber, pointWithinPolygon } from '@lifk/utils'

/*
  Remedy Forecast Calculations
*/
const levelToPoints: Record<TEffectLevel, number> = {
  XS: 10,
  '-XS': -10,
  S: 40,
  '-S': -40,
  M: 160,
  '-M': -160,
  L: 640,
  '-L': -640,
  XL: 2560,
  '-XL': -2560
}

const mapLevelToPoints = (level: TEffectLevel) => {
  const points = level ? (levelToPoints[level] ?? 0) : 0
  return points
}

// Calculates effect points and remedy total points and total cost.
const getRemedyWithForecastTotals = (remedy: IRemedy) => {
  let totalPoints = 0
  let totalCost = 0

  remedy.effects.forEach((effect) => {
    const { level } = effect
    const points = mapLevelToPoints(level as TEffectLevel)
    effect.points = points
    totalPoints += points
  })

  remedy.budgets.forEach((cost) => {
    totalCost += cost.cost
  })

  remedy.totalPoints = totalPoints
  remedy.totalCost = totalCost
  return remedy
}

export const getFootprintModifier = (
  remedy: IRemedy,
  customFootprintSqFt: number
) => {
  const defaultFootprint = remedy.footprint
  if (
    customFootprintSqFt &&
    defaultFootprint > 0 &&
    customFootprintSqFt !== defaultFootprint
  ) {
    return customFootprintSqFt / defaultFootprint
  }
  return 1
}

export const getWeightedTotalCost = (
  remedy: IRemedy,
  customFootprintSqFt: number
) => remedy.totalCost * getFootprintModifier(remedy, customFootprintSqFt)

/*
  Remedy Size/Footprint Calculations
*/
const getRemedySizeInt = (remedyFootprint: number) =>
  remedyFootprint.toString().match(/[0]/g)?.length ?? 0

export const getRemedySize = (remedyFootprint: number) => {
  const numZeroes = getRemedySizeInt(remedyFootprint)
  switch (numZeroes) {
    case 4:
      return 'XL'
    case 3:
      return 'L'
    case 2:
      return 'M'
    case 1:
      return 'S'
    default:
      return 'M'
  }
}

export const getOverflowingFootprintPlacements = (
  mappedRemedies: IMappedRemedy[]
) => {
  const allOverflowingFootprintPlacements = mappedRemedies
    .filter(
      (mr) =>
        mr.isWithinPolygon &&
        mr.remedy.sumRemediesExceedsSiteFootprint &&
        mr.remedy.footprintPlacement
    )
    .map((mr) => mr.remedy.footprintPlacement)
  return Array.from(new Set(allOverflowingFootprintPlacements))
}

export const getFootprintOverflowStates = (
  remedy: IRemedy,
  siteOccupiedFootprintAreas: Record<TFootprintPlacement, number> | undefined,
  siteArea: number,
  includedInTotals: boolean
) => {
  const competingRemedyFootprints =
    remedy.footprintPlacement && siteOccupiedFootprintAreas
      ? siteOccupiedFootprintAreas[remedy.footprintPlacement]
      : 0

  const exceedsSiteFootprint = siteArea < remedy.footprint

  // if remedy is already mapped, it is already included in competingRemedyFootprints.
  // if not mapped, check if adding that remedy would exceed the site's footprint
  const sumRemediesExceedsSiteFootprint =
    siteArea <
    competingRemedyFootprints + (includedInTotals ? 0 : remedy.footprint)

  return {
    exceedsSiteFootprint,
    sumRemediesExceedsSiteFootprint
  }
}

/*
  Sorting
*/
// Sort by sort order, else default to end of list
export const sortBySortOrder = (entities: { sortOrder: number }[]) =>
  entities.sort((a, b) => (a.sortOrder ?? 100) - (b.sortOrder ?? 100))

export const sortItemsByPoints = (
  items: (IEffectRemedy | IRemedyEffect)[],
  ignoreSign = false
): (IEffectRemedy | IRemedyEffect)[] => {
  return items.sort((a, b) => {
    const aValue = ignoreSign ? Math.abs(a.points) : a.points
    const bValue = ignoreSign ? Math.abs(b.points) : b.points
    return bValue - aValue
  })
}

/*
  Merge Data
*/
const isIRemedy = (
  deprecatedRemedy: IRemedy | { key: string }
): deprecatedRemedy is IRemedy =>
  (deprecatedRemedy as IRemedy).category !== undefined &&
  (deprecatedRemedy as IRemedy).category.id !== undefined

export const getRemedyWithAssociations = (
  remedy: IBaseRemedy | IBaseRemedyReference | undefined,
  remedyCategories: IRemedyCategory[],
  effects: IBaseEffect[],
  costs: ICost[],
  deprecatedFallback?: IRemedy | { key: string }
): IRemedy | IRemedyReference | undefined => {
  // deprecatedFallback is used for very old remedies that do not exist in Sanity,
  //   but do have old remedy data saved in the db
  const deprecatedIsIRemedy =
    deprecatedFallback && isIRemedy(deprecatedFallback)

  const fullBaseRemedy:
    | IBaseRemedy
    | IBaseRemedyReference
    | IRemedy
    | undefined =
    remedy ?? (deprecatedIsIRemedy ? deprecatedFallback : undefined)
  if (!fullBaseRemedy) return

  const fullRemedyIsIRemedy = isIRemedy(fullBaseRemedy)

  const defaultCategory: IRemedyCategory =
    remedyCategories.find(
      ({ id }) => id === '559fcfef-b14d-4779-a3db-623b225746c4' // Custom category
    ) ?? staticDefaultRemedyCategory // DEPRECATED, only use as fallback

  let category =
    remedyCategories.find(
      (c) =>
        c.id ===
        (fullRemedyIsIRemedy
          ? fullBaseRemedy.category.id
          : fullBaseRemedy.category)
    ) ?? defaultCategory

  // for very old remedies, replace old default with new better defaults
  if (category.id === 'remedy-category-default') {
    category = {
      ...category,
      ...defaultCategory
    }
  }

  const fullEffects: IRemedyEffect[] = fullBaseRemedy.effects
    .map(({ key, level }) => {
      const matchedEffect = effects.find((fullEffect) => fullEffect.key === key)
      if (matchedEffect) {
        return {
          ...matchedEffect,
          level,
          points: 0 // filled in by getRemedyWithForecastTotals
        }
      } else {
        return fullRemedyIsIRemedy
          ? fullBaseRemedy.effects.find((e) => e.key === key)
          : undefined
      }
    })
    .filter((effectOrUndefined) => !!effectOrUndefined)

  const fullCosts: IRemedyCost[] = (fullBaseRemedy.budgets ?? [])
    .map(({ key, cost }) => {
      const matchedCost = costs.find((fullCost) => fullCost.key === key)
      if (matchedCost) {
        return {
          ...matchedCost,
          cost
        }
      } else {
        return fullRemedyIsIRemedy
          ? fullBaseRemedy.budgets.find((c) => c.key === key)
          : undefined
      }
    })
    .filter((costOrUndefined) => !!costOrUndefined)

  const typeSafeFootprint =
    typeof fullBaseRemedy.footprint !== 'number'
      ? isNumber(fullBaseRemedy.footprint)
        ? parseInt(fullBaseRemedy.footprint as string) // for very old string footprints
        : 100
      : fullBaseRemedy.footprint

  const withAssociations: IRemedy | IRemedyReference = {
    ...fullBaseRemedy,
    footprint: typeSafeFootprint,
    category,
    budgets: fullCosts,
    effects: fullEffects,
    totalPoints: 0, // filled in by getRemedyWithForecastTotals
    totalCost: 0 // filled in by getRemedyWithForecastTotals
  }
  return getRemedyWithForecastTotals(withAssociations)
}

export const getFullMappedRemedy = (
  mappedRemedy: IBaseMappedRemedy,
  remedies: IRemedy[],
  sites: { _id: string; coordinates: number[][] }[]
): IMappedRemedy | undefined => {
  // Is mapped remedy within the site boundaries?
  const site = sites.find((site) => site._id === mappedRemedy.sketchId)
  const isWithinPolygon = site
    ? pointWithinPolygon(mappedRemedy.location.coordinates, site.coordinates)
    : true

  // Merge CMS and db remedies
  const sanityRemedy = remedies.find(
    (remedy) => remedy.key === mappedRemedy.remedy.key
  )
  const fallbackDeprecatedRemedy = isIRemedy(mappedRemedy.remedy)
    ? mappedRemedy.remedy
    : undefined
  const fullRemedyObj = sanityRemedy ?? fallbackDeprecatedRemedy
  if (!fullRemedyObj) return undefined

  const fullMappedRemedy: IMappedRemedy = {
    ...mappedRemedy,
    isWithinPolygon,
    remedy: {
      ...fullRemedyObj,
      // to be filled in later if needed (see useGetFullMappedRemedies)
      exceedsSiteFootprint: false,
      sumRemediesExceedsSiteFootprint: false
    }
  }
  return fullMappedRemedy
}
